import { Box, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

import { useGoogleMapsApiLoader } from '@frontend/features/common/hooks/useGoogleMapsApi';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { usePatchHotel } from '@frontend/features/hotel-content-manager/mutations';
import { useGetHotelNimbuxHotelById } from '@frontend/features/hotel-content-manager/queries';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { LoadingSkeleton } from '@frontend/features/hotel-content-manager/components/Jobs';
import { LoadingButton } from '@mui/lab';
import { FieldWrapper } from '@frontend/features/hotel-content-manager';

interface Coords {
  lat: number;
  lng: number;
}

const zoom = 14;

export const HotelContentLocation: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const { hotelId } = useParams();
  
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);
  const [address, setAddress] = useState<string>('');
  const [mapMarker, setMapMarker] = useState<Coords | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded: mapLoaded } = useGoogleMapsApiLoader();

  const { data: nimbuxHotel, isLoading: isLoadingNimbuxHotel } = useGetHotelNimbuxHotelById(Number(hotelId));
  const { mutate: patchHotel, isLoading: isUpdatingHotel } = usePatchHotel(
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotelById, Number(hotelId)]);
        addToast({
          color: 'success',
          message: t('hotelContent.view.location.messages.updateSuccess'),
        });
      },
      onError: () => {
        addToast({
          color: 'error',
          message: t('hotelContent.view.location.messages.updateError'),
        });
      },
    }
  );
  
  const center = useMemo(() => {
    const lat = latitude;
    const lng = longitude;
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return { lat: 0, lng: 0 };
  }, [latitude, longitude]);

  const handleLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMapClick = ({ latLng }: google.maps.MapMouseEvent) => {
    if (latLng) {
      const lat = latLng.lat();
      const lng = latLng.lng();
      
      setLatitude(Number(lat.toFixed(6)));
      setLongitude(Number(lng.toFixed(6)));
    }
  };

  const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      setLatitude(Number(lat.toFixed(6)));
      setLongitude(Number(lng.toFixed(6)));
    }
  };

  const handleLatitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLatitude(Number(Number(value).toFixed(6)));
  };

  const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLongitude(Number(Number(value).toFixed(6)));
  };

  const handleSave = () => {
    patchHotel({
      id: Number(hotelId),
      address: address,
      latitude: latitude,
      longitude: longitude,
    });
  };

  useEffect(() => {
    const lat = latitude;
    const lng = longitude;
    
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapMarker({ lat, lng });
      if (mapRef.current) {
        mapRef.current.panTo({ lat, lng });
      }
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (nimbuxHotel) {
      setLatitude(Number(nimbuxHotel.latitude?.toFixed(6)) || 0);
      setLongitude(Number(nimbuxHotel.longitude?.toFixed(6)) || 0);
      setAddress(nimbuxHotel.address || '');
    }
  }, [nimbuxHotel]);

  return (
    <Paper elevation={0} sx={{ mb: 8, maxWidth:1082 }}>
      { isLoadingNimbuxHotel && <LoadingSkeleton /> }
      { !isLoadingNimbuxHotel && nimbuxHotel && (
        <Box display='flex' flexDirection='column' gap={5} p={2} borderRadius={2} >
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            
            <Typography variant="h6">{t('hotelContent.view.location.title')}</Typography>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              size='small'
              onClick={handleSave}
              loading={isUpdatingHotel}
            >
              {t('common.form.save')}
            </LoadingButton>
          </Box>
          <FieldWrapper label={t('hotelContent.view.location.address')} applyMaxWidth>
            <TextField 
              size='small' 
              fullWidth 
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </FieldWrapper>
          <FieldWrapper label={t('hotelContent.view.location.latitude')} applyMaxWidth>
            <TextField 
              size='small' 
              fullWidth 
              value={latitude}
              onChange={handleLatitudeChange}
              type="number"
              inputProps={{ step: "any" }}
            />
          </FieldWrapper>
          <FieldWrapper label={t('hotelContent.view.location.longitude')} applyMaxWidth>
            <TextField 
              size='small' 
              fullWidth 
              value={longitude}
              onChange={handleLongitudeChange}
              type="number"
              inputProps={{ step: "any" }}
            />
          </FieldWrapper>
          <FieldWrapper label={t('hotelContent.view.location.map')}>
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('hotelContent.view.location.dragInstructions')}
              </Typography>
              <Box 
                width='100%'
                height='400px'
                borderRadius={theme.shape.borderRadius}
                position='relative'
                zIndex={1}
                sx={{ isolation:'isolate'}}
              >
                {mapLoaded && (
                  <Box
                    width='100%'
                    height='100%'
                    position='relative'
                    overflow='hidden'
                  >
                    <GoogleMap
                      mapContainerStyle={{
                        width: '100%',
                        height: '400px',
                        position: 'relative',
                      }}
                      center={center}
                      zoom={zoom}
                      onClick={handleMapClick}
                      onLoad={handleLoad}
                      onUnmount={handleUnmount}
                      options={{
                        mapTypeControl: false,
                        streetViewControl: false,
                        zoomControl: true,
                      }}
                    >
                      {mapMarker && (
                        <MarkerF 
                          position={mapMarker} 
                          key={`${mapMarker.lat},${mapMarker.lng}`} 
                          draggable 
                          onDragEnd={handleMarkerDragEnd}
                        />
                      )}
                      
                    </GoogleMap>
                  </Box>
                )}
                {!mapLoaded && (
                  <Box>
                    <Typography color="text.secondary">
                      {t('hotelContent.view.location.loadingMap')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </FieldWrapper>
        </Box>
      )}
    </Paper>
  );
};
